import Component from '@glimmer/component';

import { Entities } from 'cio/utils/metrics/common';
import { Metric, summarizeMetrics } from 'cio/utils/metrics/models/metric';
import { TestMetricType } from 'cio/models/newsletter';
import NewsletterModel from 'cio/models/newsletter';
import TemplateModel from 'cio/models/template';
import { inject as service } from '@ember/service';
import EnvironmentService from 'cio/services/environment';
import {
  getDefaultTemplateForTestGroup,
  getTestGroupTemplates,
  getTranslationTemplatesForTestGroup,
  getTestGroupIds,
} from 'cio/utils/newsletter-testgroups';

interface TemplateWithMetric {
  template: TemplateModel;
  metric: Metric;
}

interface TestGroupWithTemplatesAndMetrics {
  id: string;
  groupMetric: Metric;
  defaultTemplateWithMetric: TemplateWithMetric;
  translationTemplatesWithMetrics: TemplateWithMetric[];
}

interface ResultsArgs {
  metrics: Metric[];
  highlightedRow?: TestMetricType;
  highlightedColumn?: number;
  canSendToRemaining?: boolean;
  sentToRemaining?: boolean;
  newsletter: NewsletterModel;
  toggleSendToRemainingModal?: () => unknown;
  isLoading?: boolean;
}

export default class Results extends Component<ResultsArgs> {
  @service environment!: EnvironmentService;

  get metricsByTemplateId() {
    return this.metrics.reduce<Record<string, Metric>>((acc, metric) => {
      acc[String(metric.entityId)] = metric;
      return acc;
    }, {});
  }

  get testGroupsWithTemplatesAndMetrics(): TestGroupWithTemplatesAndMetrics[] {
    if (this.args.isLoading ?? false) {
      return [];
    }

    let items: TestGroupWithTemplatesAndMetrics[] = [];

    this.testGroupIds.forEach((testGroupId: string) => {
      let defaultTemplate = getDefaultTemplateForTestGroup(testGroupId, this.templates);
      if (!defaultTemplate) {
        return;
      }

      let translationTemplatesWithMetrics: TemplateWithMetric[] = getTranslationTemplatesForTestGroup(
        testGroupId,
        this.templates,
      ).map((template) => ({
        template,
        metric: this.metricsByTemplateId[template.id],
      }));

      let groupMetric: Metric;
      if (translationTemplatesWithMetrics.length === 0) {
        groupMetric = this.metricsByTemplateId[defaultTemplate.id];
      } else {
        let metricsToSummarize = getTestGroupTemplates(testGroupId, this.templates).map(
          (template) => this.metricsByTemplateId[template.id],
        );
        groupMetric = summarizeMetrics(this.args.newsletter.id, Entities.NEWSLETTER, null, metricsToSummarize);
      }

      items.push({
        id: testGroupId,
        groupMetric,
        defaultTemplateWithMetric: {
          template: defaultTemplate,
          metric: this.metricsByTemplateId[defaultTemplate.id],
        },
        translationTemplatesWithMetrics,
      });
    });

    return items;
  }

  get testGroupIds() {
    return getTestGroupIds(this.templates);
  }

  get templates(): TemplateModel[] {
    return (this.args.newsletter.templates.content as TemplateModel[] | undefined) ?? [];
  }

  get metrics() {
    return this.args.metrics ?? [];
  }
}
